import {
  format,
  isAfter,
  isBefore,
  isPast,
  isSameDay,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import type { DayProps } from 'react-day-picker';
import { useDayRender } from 'react-day-picker';
import type {
  MenuListProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { components } from 'react-select';
import InsightsRestriction from '../admin/insights/InsightsRestriction';
import InsightsTimeOption from '../admin/insights/InsightsTimeOption';
import useAppSelector from '../app/redux/useAppSelector';
import type { PlanTier } from '../graphql/generated/graphql';
import { UsageAnalyticsDays } from '../organization/PricingConfig';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import Caret from './Caret';
import styles from './DateRangePicker.module.scss';
import DayButton from './DayButton';
import DayPicker from './DayPicker';
import Select from './Select';
import PopOver from './deprecated/PopOver';
import PopOverContainer from './deprecated/PopOverContainer';

type TimeRange = {
  label: string;
  value: number | 'custom';
};

type TimeRanges = readonly { options: readonly TimeRange[] }[];

const DEFAULT_TIME_RANGES = [
  {
    options: [
      { label: '7 days', value: 7 },
      { label: '14 days', value: 14 },
      { label: '30 days', value: 30 },
      { label: '90 days', value: 90 },
    ],
  },
  {
    options: [{ label: 'Custom', value: 'custom' }],
  },
] as const;

type Dates = {
  startAt: Date | null;
  endAt: Date | null;
};

const getNewDates = ({
  startDate = null,
  endDate = null,
  defaultStartAt,
  defaultEndAt,
}: {
  startDate?: Date | null;
  endDate?: Date | null;
  defaultStartAt?: never;
  defaultEndAt?: never;
}): Dates => {
  const newStartAt = startDate ? startOfDay(startDate) : defaultStartAt;

  let newEndAt = endDate ? startOfDay(endDate) : defaultEndAt;

  if (startDate && newStartAt && newEndAt && newStartAt > newEndAt) {
    newEndAt = startOfDay(startDate);
  }

  return {
    startAt: newStartAt ?? null,
    endAt: newEndAt ?? null,
  };
};

const getDatesForNumberOfDays = (days: number) =>
  getNewDates({
    startDate: subDays(new Date(), days),
    endDate: subDays(new Date(), 1),
  });

export const DEFAULT_TIME_RANGE = DEFAULT_TIME_RANGES[0].options[2].value;
export const DEFAULT_DATES = getDatesForNumberOfDays(DEFAULT_TIME_RANGE) as {
  startAt: Date;
  endAt: Date;
};

const IconLeft = () => <Caret direction="left" block size={10} />;
const IconRight = () => <Caret direction="right" block size={10} />;

interface DateRangePickerProps {
  ranges?: TimeRanges;
  onDatesChange: (dates: {
    startAt: moment.Moment;
    endAt: moment.Moment;
  }) => void;
  firstAllowedDate?: moment.Moment;
  initialRange?: TimeRange['value'];
}

const DateRangePicker = ({
  ranges = DEFAULT_TIME_RANGES,
  onDatesChange,
  firstAllowedDate,
  initialRange = DEFAULT_TIME_RANGE,
}: DateRangePickerProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [timeRange, setTimeRange] = useState<TimeRange['value']>(initialRange);

  const [dates, setDates] = useState<Dates>(
    getDatesForNumberOfDays(typeof timeRange === 'number' ? timeRange : 7),
  );

  const [stage, setStage] = useState<'startDate' | 'endDate' | null>(null);
  const isDayPickerOpen = !!stage;

  const isLarge = useIsSize(Sizes.LG);

  const handleChangeDates = (newDates: Dates) => {
    setDates(newDates);
    if (newDates.startAt && newDates.endAt) {
      onDatesChange({
        startAt: moment(newDates.startAt),
        endAt: moment(newDates.endAt),
      });
    }
  };

  const handleTimeRangeChange = (value: TimeRange['value'] | null) => {
    if (!value) {
      return;
    }
    if (typeof value === 'number') {
      handleChangeDates(getDatesForNumberOfDays(value));
    }
    if (value === 'custom') {
      setStage('startDate');
    }
    setTimeRange(value);
  };

  const defaultValue = useMemo(
    () =>
      ranges
        .flatMap((r) => r.options)
        .find((range) => range.value === initialRange),
    [ranges, initialRange],
  );

  const datesString = `${
    dates.startAt ? format(dates.startAt, 'MMM d') : ''
  } - ${dates.endAt ? format(dates.endAt, 'MMM d') : ''}`;

  const range = {
    from: dates.startAt ?? undefined,
    to: dates.endAt ?? undefined,
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const Day = (props: DayProps) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);
    const planTier = useAppSelector(
      (state) => state.session.organization.planTier,
    ) as PlanTier;

    if (dayRender.isHidden) {
      // react-day-picker requires an empty fragment
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }
    if (!dayRender.isButton) {
      return <div {...dayRender.divProps} />;
    }

    const isDisabled = dayRender.activeModifiers.disabled && isPast(props.date);

    const dayView = (
      <DayButton
        activeModifiers={dayRender.activeModifiers}
        onClick={dayRender.buttonProps.onClick}
        disabled={isDisabled}
      >
        {dayRender.buttonProps.children}
      </DayButton>
    );

    if (isDisabled) {
      return (
        // @ts-expect-error
        <PopOver
          arrow={<PopOverContainer.Arrow />}
          placement="top"
          popOver={
            <InsightsRestriction
              onUpgradeClick={() => {
                setStage(null);
              }}
              olderThan={UsageAnalyticsDays[planTier]}
            />
          }
          preventCloseOnContentClick
          trigger="hover"
          popOverPortal={document.body}
        >
          {dayView}
        </PopOver>
      );
    }
    return dayView;
  };

  return (
    <div>
      <div className={styles.selectWrapper}>
        {/* @ts-expect-error */}
        <PopOver
          placement="bottom-end"
          onOpenChange={() => isDayPickerOpen && setStage(null)}
          open={isDayPickerOpen}
          trigger="click"
          preventCloseOnContentClick
          popOver={
            timeRange === 'custom' && (
              <div className={styles.dayPickerWrapper}>
                <PopOverContainer width="auto" noPadding>
                  <DayPicker
                    mode="range"
                    numberOfMonths={isLarge ? 2 : 1}
                    selected={range}
                    modifiers={{
                      first: range.from ?? false,
                      last: range.to ?? range.from ?? false,
                    }}
                    disabled={{
                      after: new Date(),
                      before: firstAllowedDate?.toDate(),
                    }}
                    onSelect={(newRange) => {
                      const changedDate =
                        newRange?.from &&
                        range.from &&
                        isSameDay(newRange.from, range.from)
                          ? newRange.to ?? null
                          : newRange?.from ?? null;

                      if (!changedDate) {
                        return;
                      }

                      if (stage === 'startDate') {
                        handleChangeDates(
                          getNewDates({
                            startDate: changedDate,
                            endDate:
                              (range.from &&
                                isBefore(changedDate, range.from)) ||
                              (range.to && isAfter(changedDate, range.to))
                                ? null
                                : range.to,
                          }),
                        );

                        setStage('endDate');
                        return;
                      }
                      if (range.from && isBefore(changedDate, range.from)) {
                        handleChangeDates(
                          getNewDates({
                            startDate: changedDate,
                            endDate: null,
                          }),
                        );
                        return;
                      }
                      handleChangeDates(
                        getNewDates({
                          startDate: range?.from ?? null,
                          endDate: newRange?.to ?? changedDate ?? null,
                        }),
                      );
                      setTimeout(() => {
                        setStage(null);
                      }, 100);
                    }}
                    defaultMonth={
                      isLarge
                        ? subMonths(startOfMonth(new Date()), 1)
                        : new Date()
                    }
                    components={{ Day, IconLeft, IconRight }}
                  />
                </PopOverContainer>
              </div>
            )
          }
        >
          <Select<TimeRange, false>
            className={styles.select}
            isClearable={false}
            defaultValue={defaultValue}
            onChange={handleTimeRangeChange}
            menuIsOpen={isMenuOpen}
            options={ranges}
            onMenuOpen={() => {
              setStage(null);
              setIsMenuOpen(true);
            }}
            onMenuClose={() => setIsMenuOpen(false)}
            isSearchable={false}
            value={timeRange}
            components={{
              // eslint-disable-next-line react/no-unstable-nested-components
              Option: (props: OptionProps<TimeRange, false>) => (
                <InsightsTimeOption
                  {...props}
                  onUpgradeClick={() => setIsMenuOpen(false)}
                />
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              MenuList: (props: MenuListProps<TimeRange, false>) => (
                <components.MenuList {...props}>
                  <div className={styles.titleContainer}>
                    <div className={styles.title}>
                      {typeof timeRange === 'number'
                        ? `Last ${timeRange} days`
                        : props.getValue()[0]?.label}
                    </div>
                    <div className={styles.titleDates}>{datesString}</div>
                  </div>
                  {props.children}
                </components.MenuList>
              ),
              // eslint-disable-next-line react/no-unstable-nested-components
              GroupHeading: () => <hr className={styles.groupHeading} />,
              // eslint-disable-next-line react/no-unstable-nested-components
              SingleValue: (props: SingleValueProps<TimeRange, false>) => (
                <components.SingleValue {...props}>
                  {typeof timeRange === 'number' ? props.children : datesString}
                </components.SingleValue>
              ),
            }}
          />
        </PopOver>
      </div>
    </div>
  );
};

export default DateRangePicker;
